import {
  isRideToSurviveActivation,
  RIDE_TO_SURVIVE_ACTIVATION_ID,
  RIDE_TO_SURVIVE_PRODUCTION_ACTIVATION_ID,
} from "../../../api/src/auth/otpTemplates.js";
import type { LegalDraft } from "./legalDraft.js";
import { documentBodyLooksUnfinished } from "./legalDraft.js";
import type { LegalSetup } from "./legalSetup.js";

/**
 * Ride to Survive on XP1 staging and production. Consent ticks on the live
 * bundle are already real. The signed document bodies were lorem. Filling
 * the Legal panel for this activation, then publishing, is what a rider
 * reads. Other activations keep whatever they already have.
 */
export const RIDE_ACTIVATION_ID = RIDE_TO_SURVIVE_ACTIVATION_ID;
export { RIDE_TO_SURVIVE_PRODUCTION_ACTIVATION_ID };

export const RIDE_DOCUMENT_HREF = {
  terms: "https://game.novaexhibits.com/legal/terms",
  privacy_notice: "https://game.novaexhibits.com/legal/privacy",
  cookie_notice: "https://game.novaexhibits.com/legal/cookies",
  official_rules: "https://game.novaexhibits.com/rules",
} as const;

const RIDE_TITLES = {
  terms: "Terms of this game",
  privacy_notice: "Privacy notice",
  cookie_notice: "Cookies",
  official_rules: "Game rules",
} as const;

type RideKind = keyof typeof RIDE_DOCUMENT_HREF;

/** Markdown the Legal panel compiles. Same facts as FortNine `/legal/*`. */
export const RIDE_DOCUMENT_BODY: Readonly<Record<RideKind, string>> = {
  terms: `# Terms of this game

Ride to Survive is a free promotional game. Playing it is not a purchase and it is not a condition of buying anything from FortNine. A discount code, if one is issued, is a promotional offer and not a prize draw.

## Who runs it

Nova Exhibits Limited (UK) and Black Peak North LLC (US) operate the game and the registration. FortNine is the brand on the stand. FortNine does not operate this site.

## The account

You register with an email you can prove you can read, a name, and a gamer tag. The tag is what other people see on the board. The name is not. The game rules are the rules of play: one account, no automated play, and scores that were not set fairly may be taken down.

## What you agree to when you register

- The score and gamer tag you submit may appear on the public leaderboard.
- The email you give is used to send a code if you qualify, and for nothing else unless you also tick the optional marketing box.
- Opening a document from the form is not agreement. Only the ticks you make are.

## What you can undo

You can delete the FortNine game account and its data from Privacy & Data at any time. A score may stay on the board only as an anonymous, unlinkable row, and unredeemed codes die with the account. This does not delete the separate attendee and consent records held in Nova XP.

## What this is not

It is not FortNine's website terms, not their store terms, and not a terms of sale. Those live with FortNine. A code is promotional. It is not cash, not transferable by us, and not an obligation on FortNine beyond whatever FortNine prints on the code.

## Contact

Questions about this game: support@novaexhibits.com
`,
  privacy_notice: `# Privacy notice

This notice is for Ride to Survive on game.novaexhibits.com. Black Peak North LLC is responsible for how the information you provide here is used. The stand and its technology are operated by Nova Exhibits on its behalf. It is not FortNine's privacy policy.

## What you type

First name, last name, a gamer tag, and an email address. The email is proved with a one-time code before anything is stored against it. Optional ticks — contact, and marketing — are recorded only if you leave them on.

## Where it goes

The name, the tag, the proved email and the ticks you grant are written to the Ride to Survive activation on Nova XP. The one-time code is sent by email. The FortNine score service separately stores the game profile, raw runs, scores and discount codes; this browser also keeps a local copy of recent game data.

FortNine does not receive your email from this form unless a separate, ticked marketing box says so.

## What is public

The gamer tag and the score beside it. Not your name, and not your email. That is the whole reason the tag is a field of its own.

## How long we keep it

Registration records are kept for 12 months, then deleted. A published leaderboard row may remain as a name and a time after the contact details are gone.

## What you can undo

Privacy & Data deletes the FortNine game profile, raw runs and unredeemed codes, unlinks retained redeemed codes, and leaves score values only in anonymous board rows. It does not delete Nova XP attendee or consent records. Cookie choices can be changed there without deleting anything.

To access, correct or delete what Nova XP holds, contact support@novaexhibits.com.

## What this page is not

It is not FortNine's privacy notice and it does not replace the consent wording on the registration step. That wording is served by the activation, and replacing it is a new published version — not an edit to this page.
`,
  cookie_notice: `# Cookies

This page uses cookies for three jobs, and only one of them is required. The other two stay off until you switch them on.

## Essential

These keep your run, your score and your rider attached to this browser, and they remember the cookie choice itself so the banner does not come back every visit. They are not a choice, because without them the game cannot tell a finished run from a refresh. The banner says so.

## Analytics

Optional. Which biomes get finished, and where runs end. Off until you accept all or save them under Manage. Nothing is sent until then. When they are on, page views go to Plausible Analytics for this hostname only. Plausible does not set a tracking cookie of its own.

## Marketing

Optional. FortNine store promotion, unrelated to the game. Off until you accept all or save them under Manage.

## Changing your mind

Signed in, the same two optional switches live under Privacy & Data. Signing out does not clear the choice — only answering the banner again, or deleting the site data in your browser, does.
`,
  official_rules: `# Game rules

Ride to Survive is a side-view run. You keep the package on the back of the bike in one piece and you finish the course. The package is the thing that can fail: three hits and the run is over. Points from pickups are extra, not the win.

This is a game of skill, not a game of chance. A finish that is in the top 1,000 of the published leaderboard, at the moment the board is read, is eligible for a promotional FortNine discount code.

## One rider, one account

One account per person. Automated play, scripted input and anything else that is not a person on the controls is out. A score that was not set fairly may be taken down, and that decision does not have to be explained on the board.

## How you ride

Forward and back with D / right arrow and A / left arrow. Hold W, up arrow or shift to wheelie — that lifts the front wheel over low obstacles. Space or J punches. On a phone the same four actions are the pads on the screen.

## What hits the package

Crashing into something you did not clear, falling off a platform, and walking into a zombie you did not punch. A hit knocks you down for a moment. Three hits ends the run wherever you are.

## The code

Top ten on the monthly board earns 20% off. Top hundred earns 15%. Any other qualifying finish earns 10%. The board resets each month; all-time records stay. The code is emailed to the address you verified, and it also sits under My codes. A code is for one use at FortNine and is not a cash prize. Nova Exhibits does not sell it and does not keep a commission on it.

## The board

Your gamer tag and your score are public. Season boards reset monthly. All-time records persist. Removing your account anonymises the tag; the score stays. A pull from the desk takes a row off a wall. That is not a refund and not a finding that the ride was unfair.

## Contact

support@novaexhibits.com
`,
};

function isRideKind(kind: string): kind is RideKind {
  return kind === "terms" || kind === "privacy_notice" || kind === "cookie_notice" || kind === "official_rules";
}

export function rideDocumentDraft(kind: RideKind): LegalDraft {
  return {
    kind,
    enabled: true,
    title: RIDE_TITLES[kind],
    href: RIDE_DOCUMENT_HREF[kind],
    body: RIDE_DOCUMENT_BODY[kind],
    version: "2",
  };
}

/**
 * Put finished Ride copy on any document that is still empty, lorem, or the
 * placeholder sentence. A later finished edit is left alone.
 */
export function withRideLegalDocuments(setup: LegalSetup, activationId: string): LegalSetup {
  if (!isRideToSurviveActivation(activationId)) return setup;
  let changed = false;
  const documents = setup.documents.map((draft) => {
    if (!isRideKind(draft.kind)) return draft;
    const finished = draft.enabled && !documentBodyLooksUnfinished(draft.body);
    if (finished) return draft;
    if (!draft.enabled && !documentBodyLooksUnfinished(draft.body) && draft.body.trim() !== "") {
      return draft;
    }
    changed = true;
    return rideDocumentDraft(draft.kind);
  });
  if (!changed) return setup;
  return {
    ...setup,
    documents,
    packs: {
      UK: withRideDocumentUrls(setup.packs.UK),
      US: withRideDocumentUrls(setup.packs.US),
    },
  };
}

function withRideDocumentUrls<T extends { tokens: Record<string, string> }>(pack: T): T {
  return {
    ...pack,
    tokens: {
      ...pack.tokens,
      TERMS_URL: RIDE_DOCUMENT_HREF.terms,
      PRIVACY_URL: RIDE_DOCUMENT_HREF.privacy_notice,
      COOKIE_URL: RIDE_DOCUMENT_HREF.cookie_notice,
      RULES_URL: RIDE_DOCUMENT_HREF.official_rules,
    },
  };
}
